import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { apiPlataforma } from "../../../repositories/api.plataforma";
import { getTokens } from "../../../services/data";

export type AuditoriaSecao = "acessos" | "modificacoes";
export interface AuditoriaPage<T> {
  data: T[];
  limit: number;
  offset: number;
  hasMore: boolean;
}

const camposAcessos = ["id", "usuario", "email", "sucesso", "data_tentativa", "ip_usuario", "razao_erro"];
const camposModificacoes = [
  "tabela",
  "id",
  "nome",
  "aud_criado_por",
  "aud_criado_data",
  "aud_modificado_por",
  "aud_modificado_data",
];

export function readAuditoriaParams(search: string, secao: AuditoriaSecao, defaultLimit: number) {
  const params = new URLSearchParams(search);
  const campos = secao === "acessos" ? camposAcessos : camposModificacoes;
  const rawLimit = Number(params.get("limit") || params.get("data_limit") || defaultLimit);
  const limit = Number.isInteger(rawLimit) && rawLimit > 0 && rawLimit <= 1000 ? rawLimit : defaultLimit;
  const rawOffset = Number(params.get("offset") || params.get("data_offset") || 0);
  const offset = Number.isSafeInteger(rawOffset) && rawOffset >= 0 ? Math.floor(rawOffset / limit) * limit : 0;
  let coluna = params.get("coluna") || params.get("filter_column") || "";
  let filtro = params.get("filtro") || params.get("filter_value") || "";
  // Keep existing bookmarked PostgREST filters working during the migration.
  if (!coluna) {
    coluna = campos.find((campo) => /^(eq\.|ilike\.\*)/.test(params.get(campo) || "")) || "";
    filtro = coluna ? (params.get(coluna) || "").replace(/^eq\.|^ilike\.\*/, "").replace(/\*$/, "") : "";
  }
  if (!campos.includes(coluna) || !filtro.trim()) {
    coluna = "";
    filtro = "";
  }
  const [legacyOrderBy, legacyDirection] = (params.get("order") || "").split(".");
  const requestedOrderBy = params.get("orderBy") || legacyOrderBy;
  const orderBy = campos.includes(requestedOrderBy) ? requestedOrderBy : "id";
  const direction: "asc" | "desc" = (params.get("direction") || legacyDirection) === "asc" ? "asc" : "desc";
  return { limit, offset, coluna, filtro, orderBy, direction };
}

export default function useAuditoriaSistema<T>(secao: AuditoriaSecao, isOpen: boolean, defaultLimit: number) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = readAuditoriaParams(location.search, secao, defaultLimit);
  const [colunaSelecionada, setColunaSelecionada] = useState(params.coluna);
  const [filtro, setFiltro] = useState(params.filtro);
  const tokens = getTokens();
  const scope = [tokens["x-tn-token"], tokens["x-auth-token"]];
  const endpoint = `/auditoria/sistema/${secao}`;

  useEffect(() => {
    if (isOpen) {
      setColunaSelecionada(params.coluna);
      setFiltro(params.filtro);
    }
  }, [isOpen, params.coluna, params.filtro]);

  const query = useQuery({
    queryKey: ["auditoria-sistema", ...scope, secao, params],
    queryFn: async ({ signal }) => {
      const response = await apiPlataforma.get<AuditoriaPage<T>>(endpoint, { params, signal });
      return response.data;
    },
    staleTime: 120000,
    enabled: isOpen,
  });
  const total = useQuery({
    // Count is independent of page and order, so pagination reuses the cached total.
    queryKey: ["auditoria-sistema-total", ...scope, secao, params.coluna, params.filtro],
    queryFn: async ({ signal }) => {
      const response = await apiPlataforma.get<{ totalRecords: number }>(`${endpoint}/total`, {
        params: { coluna: params.coluna, filtro: params.filtro },
        signal,
      });
      return response.data.totalRecords;
    },
    staleTime: 120000,
    enabled: isOpen,
  });

  function update(next: Partial<typeof params>) {
    const search = new URLSearchParams();
    const municipio = new URLSearchParams(location.search).get("municipio");
    if (municipio) search.set("municipio", municipio);
    search.set("secao", secao);
    Object.entries({ ...params, ...next }).forEach(([key, value]) => {
      if (value !== "") search.set(key, String(value));
    });
    navigate({ pathname: location.pathname, search: search.toString() });
  }

  function isValidSearch() {
    const value = filtro.trim();
    if (!colunaSelecionada || !value || value.length > 500) return false;
    if (colunaSelecionada === "id") return /^\d+$/.test(value);
    if (colunaSelecionada === "sucesso") return /^(sim|não|nao|true|false)$/i.test(value);
    return true;
  }

  async function getAuditoriaExport(): Promise<T[]> {
    const rows: T[] = [];
    const assertSession = () => {
      const current = getTokens();
      if (current["x-tn-token"] !== tokens["x-tn-token"] || current["x-auth-token"] !== tokens["x-auth-token"]) {
        throw new Error("A sessão mudou durante a exportação. Inicie a exportação novamente.");
      }
    };
    let offset = 0;
    let hasMore = true;
    while (hasMore) {
      assertSession();
      const response = await apiPlataforma.get<AuditoriaPage<T>>(endpoint, {
        params: { ...params, limit: 1000, offset },
      });
      assertSession();
      rows.push(...response.data.data);
      hasMore = response.data.hasMore && response.data.data.length > 0;
      offset += response.data.data.length;
    }
    return rows;
  }

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    totalRecords: total.data ?? -1,
    hasMore: query.data?.hasMore ?? false,
    colunaSelecionada,
    setColunaSelecionada,
    filtro,
    setFiltro,
    page: params.offset / params.limit,
    rowsPerPage: params.limit,
    order: params.direction,
    orderBy: params.orderBy,
    isValidSearch,
    handleSearch: () => {
      if (isValidSearch()) update({ coluna: colunaSelecionada, filtro: filtro.trim(), offset: 0 });
    },
    handleRequestSort: (property: string) =>
      update({
        orderBy: property,
        direction: params.orderBy === property && params.direction === "asc" ? "desc" : "asc",
        offset: 0,
      }),
    clearFilters: () => {
      setColunaSelecionada("");
      setFiltro("");
      update({ coluna: "", filtro: "", offset: 0, orderBy: "id", direction: "desc" });
    },
    handleChangePage: (_event: unknown, newPage: number) => update({ offset: newPage * params.limit }),
    handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      update({ limit: Number(event.target.value), offset: 0 }),
    getAuditoriaExport,
  };
}
